import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import type { AISuggestion } from '../types/aiTypes'

// ============================================================================
// SUGGESTION CREATION
// ============================================================================

/**
 * Converts AI-generated nodes and edges to the suggestion format used by the UI.
 * Each suggestion represents a potential node that can be added to the mindmap.
 *
 * @param nodes - AI-generated nodes from the backend
 * @param edges - AI-generated edges connecting nodes to their parents
 * @param batchId - Optional batch identifier for grouping suggestions
 * @param dbIds - Optional array of database IDs for each suggestion (in same order as nodes)
 * @returns Array of suggestions ready for UI display
 */
export function createSuggestions(
  nodes: MindMapNode[],
  edges: MindMapEdge[],
  batchId?: number,
  dbIds?: string[]
): AISuggestion[] {
  const now = Date.now()
  return nodes.map((node, index) => {
    const { title, description } = formatSuggestionDisplay(node)
    return {
      id: `suggestion-${node.id}`,
      dbId: dbIds?.[index], // Database ID for API calls
      title,
      description,
      category: node.data.category,
      selected: false,
      originalNode: node,
      originalEdge: edges[index],
      batchId,
      createdAt: now, // Timestamp when this batch was generated
    }
  })
}

/**
 * Formats the title and description for a suggestion based on node category.
 */
function formatSuggestionDisplay(node: MindMapNode): {
  title: string
  description: string
} {
  return {
    title: node.data.label || formatCategoryTitle(node.data.category),
    description: '',
  }
}

/**
 * Formats a category string into a readable title.
 * Example: 'idea' -> 'Idea suggestion'
 */
function formatCategoryTitle(category: string): string {
  const capitalized = category.charAt(0).toUpperCase() + category.slice(1)
  return `${capitalized} suggestion`
}

// ============================================================================
// SUGGESTION FILTERING
// ============================================================================

/**
 * Gets suggestion labels for a specific node and operation.
 * Used to pass existing suggestions to the AI to avoid duplicates.
 *
 * @param suggestions - All current suggestions
 * @param nodeId - The source node ID
 * @param operation - The AI operation type
 * @param currentOperation - Currently active operation (from state)
 * @param lastOperation - Last completed operation (from ref)
 * @returns Array of suggestion labels (strings)
 */
export function getExistingSuggestionLabels(
  suggestions: AISuggestion[],
  nodeId: string,
  operation: string,
  currentOperation: string | null,
  lastOperation: string
): string[] {
  return suggestions
    .filter((suggestion) => {
      const edgeSourceMatches = suggestion.originalEdge?.source === nodeId
      const operationMatches = lastOperation === operation || currentOperation === operation
      return edgeSourceMatches && operationMatches
    })
    .map((suggestion) => suggestion.originalNode.data.label)
    .filter((label): label is string => Boolean(label))
}

// ============================================================================
// SUGGESTION STATE UPDATES
// ============================================================================

/**
 * Adds new suggestions to the existing list, handling batch appending.
 * When preserveSuggestions is true, appends to existing suggestions for the node.
 * Otherwise, replaces suggestions for the node while keeping others.
 *
 * @param existingSuggestions - Current suggestions array
 * @param newSuggestions - New suggestions to add
 * @param nodeId - Source node ID for the new suggestions
 * @param preserveSuggestions - Whether to append or replace
 * @returns Updated suggestions array
 */
export function mergeSuggestions(
  existingSuggestions: AISuggestion[],
  newSuggestions: AISuggestion[],
  nodeId: string,
  preserveSuggestions: boolean
): AISuggestion[] {
  const suggestionsFromOtherNodes = existingSuggestions.filter(
    (s) => s.originalEdge?.source !== nodeId
  )

  if (preserveSuggestions) {
    const existingForNode = existingSuggestions.filter((s) => s.originalEdge?.source === nodeId)
    return [...suggestionsFromOtherNodes, ...existingForNode, ...newSuggestions]
  }

  return [...suggestionsFromOtherNodes, ...newSuggestions]
}

/**
 * Removes all suggestions for a specific source node.
 *
 * @param suggestions - Current suggestions array
 * @param nodeId - Source node ID to remove suggestions for
 * @returns Filtered suggestions array
 */
export function removeSuggestionsForNode(
  suggestions: AISuggestion[],
  nodeId: string
): AISuggestion[] {
  return suggestions.filter((s) => s.originalEdge?.source !== nodeId)
}
